/**
 * ChatWidget Component - Main Chat Widget Container
 * 
 * Orchestrates all chat sub-components:
 * - FloatingButton: Main toggle button
 * - ChatHeader: Header with navigation and actions
 * - ChatMessages: Message list display
 * - ChatInput: Input field with audio recorder
 * - EmptyState: Welcome screen when no messages
 * - LinkedInInput: Special input for recommender mode
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { LinkedInInput } from '@/components/recommender/LinkedInInput';
import { 
  FloatingButton, 
  ChatHeader, 
  ChatMessages, 
  ChatInput, 
  EmptyState,
  type ChatMode,
  type Message,
} from '@/components/chat';

interface ChatWidgetProps {
  isOpen: boolean;
  isExpanded?: boolean;
  mode: ChatMode;
  welcomeNavigationState: number; // 0: linkedin input, 1: chat
  messages: Message[];
  isLoading?: boolean;
  isConnected?: boolean;
  isRecording?: boolean;
  onToggle: () => void;
  onClose: () => void;
  onReset: () => void;
  onModeChange: (mode: ChatMode) => void;
  onSendMessage: (message: string) => void;
  onSendAudio: (audio: string) => void;
  onRecordingChange: (recording: boolean) => void;
  onShowMessage: (from: string, message: string) => void;
  onLinkedInSubmit: (linkedinUrl: string) => void;
  onLinkedInSkip: () => void;
}

export function ChatWidget({
  isOpen,
  isExpanded = false,
  mode,
  welcomeNavigationState,
  messages,
  isLoading = false,
  isConnected = true,
  isRecording = false,
  onToggle,
  onClose,
  onReset,
  onModeChange,
  onSendMessage,
  onSendAudio,
  onRecordingChange,
  onShowMessage,
  onLinkedInSubmit,
  onLinkedInSkip,
}: ChatWidgetProps) {
  const [inputValue, setInputValue] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = () => {
    setIsResetting(true);
    onReset();
    setTimeout(() => setIsResetting(false), 1000);
  };

  // Responsive handling
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSmallScreen = windowWidth < 1024;

  // Determine if we should show chat interface or LinkedIn input
  const isLinkedInInputActive = mode === 'recommender' && welcomeNavigationState === 0;
  const isChatActive = mode === 'chatbot' || (mode === 'recommender' && welcomeNavigationState === 1);
  const showEmptyState = isChatActive && messages.length === 0 && !isLoading;
  const showMessages = isChatActive && messages.length > 0;

  return (
    <>
      {/* Floating Button */}
      <FloatingButton
        isOpen={isOpen}
        isExpanded={isExpanded}
        isSmallScreen={isSmallScreen}
        onClick={onToggle}
      />

      {/* Chat Container */}
      {isOpen && (
        <div 
          style={{ zIndex: 9998 }}
          className={cn(
          'fixed bottom-24 right-6',
          'w-[400px] lg:w-[520px] h-[650px]',
          'max-w-[calc(100vw-48px)] max-h-[calc(100vh-120px)]',
          'max-sm:bottom-0 max-sm:right-0 max-sm:left-0 max-sm:top-0',
          'max-sm:w-full max-sm:h-full max-sm:max-w-none max-sm:max-h-none',
          'bg-card text-card-foreground',
          'rounded-2xl max-sm:rounded-none',
          'shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] max-sm:shadow-none',
          'border border-gray-200 max-sm:border-0',
          'flex flex-col overflow-hidden',
          'animate-in slide-in-from-bottom-4 duration-300'
        )}>
          
          {/* Header */}
          <ChatHeader
            mode={mode}
            isConnected={isConnected}
            isResetting={isResetting}
            onModeChange={onModeChange}
            onReset={handleReset}
            onClose={onClose}
          />

          {/* Messages / Content Area */}
          <div 
            className={cn(
              "flex-1 overflow-x-hidden",
              isLinkedInInputActive ? 'p-0 overflow-hidden' : 'p-4 bg-muted/30 overflow-y-auto'
            )}
          >
            {/* LinkedIn Input Screen (Recommender mode only) */}
            {isLinkedInInputActive && (
              <LinkedInInput 
                onSubmit={onLinkedInSubmit}
                onSkip={onLinkedInSkip}
              />
            )}
            
            {/* Empty State */}
            {showEmptyState && (
              <EmptyState mode={mode} />
            )}
            
            {/* Messages List */}
            {showMessages && (
              <ChatMessages 
                messages={messages}
                isLoading={isLoading}
                mode={mode}
              />
            )}
          </div>

          {/* Input Area - Hide during recommender LinkedIn input */}
          {isChatActive && (
            <ChatInput
              value={inputValue}
              isLoading={isLoading}
              isConnected={isConnected}
              isRecording={isRecording}
              onChange={setInputValue}
              onSend={onSendMessage}
              onSendAudio={onSendAudio}
              onRecordingChange={onRecordingChange}
              onShowMessage={onShowMessage}
            />
          )}
        </div>
      )}
    </>
  );
}

export type { Message, ChatMode };
